import React, { useState, useEffect, useRef, Suspense, lazy } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import InfoPage from "../InfoPage";
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import MembershipBadge from '../../components/MembershipBadge';
import FounderBadge from '../../components/FounderBadge';
import CollabBadge, { CollabTrustStats, StampMark } from '../../components/CollabBadge';
import ModalPortal from '../../components/Modal/ModalPortal';
import Dropdown from '../../components/Dropdown';
import { getCloudinaryUrl } from '../../utils/cloudinary';
import BlurredGate from '../../components/BlurredGate';
import { displayHandle } from '../../utils/streamHelpers';
import { getAuthToken, decodeToken } from '../../utils/authHelpers';
import IntroVideoPreview from '../../components/IntroVideoPreview';
import './Creators.sass';
import { FavoriteInline, FavoriteFilterButton } from '../../components/FavoriteToggle';
import { toggleFavorite } from '../../utils/favorites';

// Lazy-loaded so the heavy mapbox-gl bundle only downloads when a user opens
// the Map view.
const CollabBoardMap = lazy(() => import('../../components/CollabBoardMap'));
const MAP_ENABLED = Boolean(process.env.REACT_APP_MAPBOX_TOKEN);

// Track creator profile views when modal opens
const useCreatorViewTracking = (isOpen, creator, currentUserId) => {
  useEffect(() => {
    if (isOpen && creator?.id && creator?.user_id !== currentUserId) {
      // Only track if it's not the owner viewing their own profile
      const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
      fetch(`${apiBase}/api/creator/track-view`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ creator_profile_id: creator.id })
      }).catch(err => console.error('Failed to track creator profile view:', err));
    }
  }, [isOpen, creator?.id, creator?.user_id, currentUserId]);
};

// Modal for showing creator details
const CreatorModal = ({ isOpen, onClose, creator, currentUserId, onFavoriteToggle }) => {
  const [isPortrait, setIsPortrait] = useState(null);
  const [didImageFallback, setDidImageFallback] = useState(false);

  // Reset state when creator changes or modal closes
  useEffect(() => {
    if (!isOpen || !creator) {
      setIsPortrait(null);
      setDidImageFallback(false);
    }
  }, [isOpen, creator?.id]);

  // Track creator profile views when modal opens
  useCreatorViewTracking(isOpen, creator, currentUserId);

  if (!creator) return null;

  // Get logged-in user's type from localStorage
  const loggedInUserType = typeof window !== 'undefined' 
    ? window.localStorage.getItem('postaway_user_type') || '' 
    : '';
  
  // Only show Message Creator button for Host or Creator users
  const canMessage = loggedInUserType === 'host' || loggedInUserType === 'creator';

  // Handle image load to detect aspect ratio
  const handleImageLoad = (e) => {
    const img = e.target;
    if (img.naturalWidth && img.naturalHeight) {
      setIsPortrait(img.naturalWidth < img.naturalHeight);
    }
  };

  // Handle image error - fallback to raw image URL, then default to landscape
  const handleImageError = (e) => {
    // First attempt: try the raw (non-Cloudinary) image URL
    if (!didImageFallback && creator?.image) {
      setDidImageFallback(true);
      e.currentTarget.src = creator.image;
      return;
    }
    // If fallback also failed, just show modal without image (don't close)
    setIsPortrait(false);
  };

  const shouldShowBlur = isPortrait === true;
  
  // Optimize modal images with Cloudinary (larger size for modal view)
  const optimizedModalImage = creator.image ? getCloudinaryUrl(creator.image, {
    width: 1200,
    height: 675,
    crop: 'fill',
    quality: 'auto',
    format: 'auto'
  }) : creator.image;

  return (
    <ModalPortal isOpen={isOpen} onClose={onClose} maxWidth="600px">
        {/* 16:9 Header Image with Blurred Side Panels for Portrait Images */}
        <div className="creator-modal-header" style={{ 
          justifyContent: shouldShowBlur ? 'center' : 'stretch'
        }}>
          {/* Blurred background layer - only for portrait images */}
          {shouldShowBlur && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `url(${optimizedModalImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              filter: 'blur(20px)',
              transform: 'scale(1.1)',
              zIndex: 0
            }} />
          )}
          
          {/* Main image */}
          <img 
            src={optimizedModalImage} 
            alt={creator.title}
            onLoad={handleImageLoad}
            onError={handleImageError}
            style={{ 
              position: 'relative',
              zIndex: 1,
              width: shouldShowBlur ? 'auto' : '100%',
              height: shouldShowBlur ? '100%' : '100%',
              maxWidth: shouldShowBlur ? '100%' : 'none',
              maxHeight: shouldShowBlur ? '100%' : 'none',
              objectFit: shouldShowBlur ? 'contain' : 'cover'
            }}
          />
        </div>
        
        <div className="creator-modal-content">
          <div className="creator-modal-title-section">
            <div style={{ flex: 1 }}>
              <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {creator.title}
                <FavoriteInline
                  isFavorited={creator.is_favorited || false}
                  onClick={() => onFavoriteToggle && onFavoriteToggle(creator.id)}
                  size={18}
                />
              </h2>
              {creator.handle && (
                <p>@{creator.handle}</p>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end', maxWidth: '100%' }}>
              <CollabTrustStats
                completedCollabs={creator.completed_collabs}
                averageRating={creator.average_rating}
                totalReviews={creator.total_reviews}
                compact
                reviewsHref={creator.id ? `/profile?creator_id=${encodeURIComponent(creator.id)}#reviews` : null}
              />
              <FounderBadge isFounder={creator.founder} />
              <MembershipBadge planId={creator.plan_id} planName={creator.plan_name} userType="creator" />
            </div>
          </div>

          {creator.bio && (
            <p className="creator-modal-bio">{creator.bio}</p>
          )}
          
          <div className="creator-modal-actions">
            <div className="creator-modal-buttons">
              {creator.id && (
                <button
                  className="button-profile"
                  onClick={() => { window.location.href = `/profile?creator_id=${encodeURIComponent(creator.id)}`; }}
                >
                  View Profile
                </button>
              )}
              {/* Message Creator button - only show for Host or Creator users */}
              {creator.user_id && canMessage && (
                <button
                  className="button-message"
                  onClick={() => { 
                    const params = new URLSearchParams();
                    params.set('to', creator.user_id);
                    if (creator.title) params.set('title', creator.title);
                    window.location.href = `/messages?${params.toString()}`;
                  }}
                >
                  Message Creator
                </button>
              )}
              {/* View Collab Post button - only show if creator has active collab post */}
              {creator.has_active_collab_post && creator.id && (
                <button
                  className="button-profile button-collab-post"
                  onClick={() => { window.location.href = `/collab-board?profileId=${encodeURIComponent(creator.id)}`; }}
                >
                  View Collab Post
                </button>
              )}
              {/* View Travel Post button - only show if creator has active travel post */}
              {creator.active_travel_post_id && (
                <button
                  className="button-profile"
                  onClick={() => { window.location.href = `/travel-board?travelPostId=${encodeURIComponent(creator.active_travel_post_id)}`; }}
                >
                  View Travel Post
                </button>
              )}
            </div>
            
            {/* Social Icons - Icons on the right */}
            <div className="creator-modal-socials">
              {creator.instagram_url && (
                <a
                  href={creator.instagram_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                >
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zM5.838 12a6.162 6.162 0 1 1 12.324 0 6.162 6.162 0 0 1-12.324 0zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm4.965-10.405a1.44 1.44 0 1 1 2.881.001 1.44 1.44 0 0 1-2.881-.001z"/>
                  </svg>
                </a>
              )}
              {creator.tiktok_url && (
                <a
                  href={creator.tiktok_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                >
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="currentColor">
                    <path d="M16.656 1.029c1.637-0.025 3.262-0.012 4.886-0.025 0.054 2.031 0.878 3.859 2.189 5.213l-0.002-0.002c1.411 1.271 3.247 2.095 5.271 2.235l0.028 0.002v5.036c-1.912-0.048-3.71-0.489-5.331-1.247l0.082 0.034c-0.784-0.377-1.447-0.764-2.077-1.196l0.052 0.034c-0.012 3.649 0.012 7.298-0.025 10.934-0.103 1.853-0.719 3.543-1.707 4.954l0.020-0.031c-1.652 2.366-4.328 3.919-7.371 4.011l-0.014 0c-0.123 0.006-0.268 0.009-0.414 0.009-1.73 0-3.347-0.482-4.725-1.319l0.040 0.023c-2.508-1.509-4.238-4.091-4.558-7.094l-0.004-0.041c-0.025-0.625-0.037-1.25-0.012-1.862 0.49-4.779 4.494-8.476 9.361-8.476 0.547 0 1.083 0.047 1.604 0.136l-0.056-0.008c0.025 1.849-0.050 3.699-0.050 5.548-0.423-0.153-0.911-0.242-1.42-0.242-1.868 0-3.457 1.194-4.045 2.861l-0.009 0.030c-0.133 0.427-0.21 0.918-0.21 1.426 0 0.206 0.013 0.41 0.037 0.61l-0.002-0.024c0.332 2.046 2.086 3.59 4.201 3.59 0.061 0 0.121-0.001 0.181-0.004l-0.009 0c1.463-0.044 2.733-0.831 3.451-1.994l0.010-0.018c0.267-0.372 0.45-0.822 0.511-1.311l0.001-0.014c0.125-2.237 0.075-4.461 0.087-6.698 0.012-5.036-0.012-10.060 0.025-15.083z"/>
                  </svg>
                </a>
              )}
              {creator.youtube_url && (
                <a
                  href={creator.youtube_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                >
                  <svg width="32" height="32" viewBox="0 0 20 14" fill="currentColor">
                    <path d="M19.615 3.154c-.19-.84-.75-1.5-1.577-1.692C16.264 1.1 10 .9 10 .9S3.736 1.1 2.962 1.462c-.827.192-1.387.852-1.577 1.692C1.1 5.03 1 6.5 1 6.5s.1 1.47.385 3.346c.19.84.75 1.5 1.577 1.692C3.736 11.9 10 12.1 10 12.1s6.264-.2 7.038-.562c.827-.192 1.387-.852 1.577-1.692C18.9 7.97 19 6.5 19 6.5s-.1-1.47-.385-3.346zM8.182 9.182V3.818L13.182 6.5l-5 2.682z"/>
                  </svg>
                </a>
              )}
            </div>
          </div>
        </div>
    </ModalPortal>
  );
};

// Individual Creator Card Component
const CreatorCard = ({ creator, onClick, onImageError, onFavoriteToggle }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [currentImageUrl, setCurrentImageUrl] = useState(null);
  const [hasTriedFallback, setHasTriedFallback] = useState(false);
  const imgRef = useRef(null);
  
  // Extract Instagram handle from URL
  const extractInstagramHandle = (url) => {
    if (!url) return null;
    const match = url.match(/instagram\.com\/([^/?]+)/);
    return match ? `@${match[1]}` : null;
  };

  // Resolve image URL based on environment
  const resolveImageUrl = (rawUrl) => {
    // Return null if no URL or empty string
    if (!rawUrl || rawUrl.trim() === '') return null;
    
    const isLocal = typeof window !== 'undefined' && window.location.hostname === 'localhost';
    let resolved = rawUrl.trim();
    
    if (resolved.startsWith('/')) {
      if (isLocal) {
        resolved = resolved.startsWith('/uploads/') ? `http://localhost:4000${resolved}` : resolved;
      }
      // Don't fallback to about-us.png - filter out creators without images
    }
    
    // Don't resolve to about-us.png - only show creators with valid images
    if (!resolved || resolved.trim() === '') return null;
    return resolved;
  };

  const rawImageUrl = resolveImageUrl(creator.avatar_asset_url || creator.avatar_url);
  
  // Apply Cloudinary optimization for card avatars (3:4 portrait aspect ratio)
  const optimizedImageUrl = rawImageUrl ? getCloudinaryUrl(rawImageUrl, {
    width: 450,   // 3:4 portrait ratio
    height: 600,  // Matches aspect-ratio: 3 / 4 in CSS
    crop: 'fill',
    quality: 'auto',
    format: 'auto'
  }) : null;
  
  // Initialize currentImageUrl with optimized URL, fallback to raw if Cloudinary not configured
  useEffect(() => {
    if (optimizedImageUrl && optimizedImageUrl !== rawImageUrl) {
      // Cloudinary is configured, use optimized URL first
      setCurrentImageUrl(optimizedImageUrl);
    } else if (rawImageUrl) {
      // Cloudinary not configured or same as raw, use raw URL
      setCurrentImageUrl(rawImageUrl);
    }
  }, [optimizedImageUrl, rawImageUrl]);
  
  const instagramHandle = extractInstagramHandle(creator.instagram_url);
  const displayName = creator.handle ? displayHandle(creator.handle) : (creator.first_name || instagramHandle || '@creator');

  const handleMessage = (e) => {
    e.stopPropagation();
    if (creator.user_id) {
      window.location.href = `/messages?to=${encodeURIComponent(creator.user_id)}`;
    } else {
      window.location.href = `/messages`;
    }
  };

  const handleImageError = (e) => {
    // If optimized URL failed and we haven't tried fallback yet, try original URL
    if (!hasTriedFallback && optimizedImageUrl && optimizedImageUrl !== rawImageUrl && currentImageUrl === optimizedImageUrl) {
      setHasTriedFallback(true);
      setCurrentImageUrl(rawImageUrl);
      setIsLoaded(false); // Reset loading state for fallback image
      setIsInView(true); // Ensure fallback image loads immediately
      return; // Don't mark as failed yet, try fallback first
    }
    
    // Both URLs failed, mark as failed and notify parent to filter out this creator
    setImageFailed(true);
    if (onImageError) {
      onImageError(creator.id);
    }
  };

  // Intersection Observer for lazy loading - must be called before early return
  useEffect(() => {
    if (!imgRef.current || !currentImageUrl) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '50px', // Start loading 50px before entering viewport
      }
    );

    observer.observe(imgRef.current);

    return () => {
      observer.disconnect();
    };
  }, [currentImageUrl]);
  
  // Filter out creators without images or with failed images
  if (!currentImageUrl || imageFailed) return null;

  const cardContent = (
    <div 
      className="creator-card"
      onClick={() => onClick && onClick(creator, currentImageUrl)}
    >
      <div className={`creator-image-wrapper${creator.has_new_activity ? ' has-activity' : ''}`} ref={imgRef}>
        {isInView && creator.intro_video_url ? (
          <IntroVideoPreview
            mode="card"
            posterUrl={creator.intro_video_poster_url || currentImageUrl}
            publicId={creator.intro_video_public_id}
            fullUrl={creator.intro_video_url}
            fallbackImageUrl={currentImageUrl}
            alt={displayName}
          />
        ) : isInView && (
          <img
            src={currentImageUrl}
            alt={displayName}
            referrerPolicy="no-referrer"
            crossOrigin="anonymous"
            onLoad={() => setIsLoaded(true)}
            onError={handleImageError}
            className={isLoaded ? 'loaded' : ''}
          />
        )}
        <CollabBadge
          completedCollabs={creator.completed_collabs}
          isFounder={creator.founder}
          ratingDistribution={creator.rating_distribution}
          variant="stamp-avatar"
        />
      </div>
      <div className="creator-name">
        <span>{displayName}</span>
        <FavoriteInline
          isFavorited={creator.is_favorited || false}
          onClick={() => onFavoriteToggle && onFavoriteToggle(creator.id)}
          size={16}
        />
      </div>
    </div>
  );

  // Wrap with Tippy tooltip if creator has active collab/travel post
  if (creator.has_new_activity) {
    return (
      <Tippy content="Member has an active post on the Collab or Travel board" placement="top">
        {cardContent}
      </Tippy>
    );
  }

  return cardContent;
};

const Creators = () => {
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState(null);
  const [membershipTier, setMembershipTier] = useState(null);
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [creatorModalOpen, setCreatorModalOpen] = useState(false);
  const [failedImageIds, setFailedImageIds] = useState(new Set());
  const [randomSeed, setRandomSeed] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [sortBy, setSortBy] = useState('Random');
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [verifiedOnly, setVerifiedOnly] = useState(false);

  // Grid vs Map view (map gated on a configured Mapbox token), shareable via ?view=map.
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState(() =>
    (MAP_ENABLED && searchParams.get('view') === 'map') ? 'map' : 'grid'
  );
  const [mapCreators, setMapCreators] = useState([]);
  const [mapLoading, setMapLoading] = useState(false);
  const mapFetchedRef = useRef(false);
  
  const sortOptions = [
    'Random',
    'Alphabetical',
    'Newest to Oldest',
    'Oldest to Newest',
    'Recent Activity',
    'Trending'
  ];

  // Fetch creators data
  const fetchCreators = async (pageNum, search = '', sort = sortBy) => {
    setLoading(true);
    try {
      // Use existing seed for subsequent pages, null for new searches or sort changes
      const seedToUse = (pageNum === 1 && (search !== searchTerm || sort !== sortBy)) ? null : randomSeed;
      
      // Map sort option to API sort value
      const sortMap = {
        'Random': 'random',
        'Alphabetical': 'alphabetical',
        'Newest to Oldest': 'newest',
        'Oldest to Newest': 'oldest',
        'Recent Activity': 'recent_activity',
        'Trending': 'popular'
      };
      
      const headers = { 'Content-Type': 'application/json' };
      const token = getAuthToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch('/api/catalogue/creators', {
        method: 'POST',
        headers,
        body: JSON.stringify({ 
          page: pageNum, 
          limit: 20, 
          search,
          sort: sortMap[sort] || 'alphabetical',
          seed: seedToUse,
          favoritesOnly,
          verifiedOnly 
        }),
      });

      if (response.ok) {
        const data = await response.json();
        
        // Store the seed from the response
        if (data.seed !== undefined) {
          setRandomSeed(data.seed);
        }
        
        if (pageNum === 1) {
          setCreators(data.creators || []);
        } else {
          setCreators(prev => [...prev, ...(data.creators || [])]);
        }
        
        setHasMore(data.hasMore || false);
        setTotal(data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching creators:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch the full geocoded creator set for the map (all eligible creators at
  // once; geocoding is cached server-side so this is cheap after first load).
  const fetchMapCreators = async () => {
    setMapLoading(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      const token = getAuthToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch('/api/catalogue/creators', {
        method: 'POST',
        headers,
        body: JSON.stringify({ mapMode: true }),
      });
      if (response.ok) {
        const data = await response.json();
        setMapCreators(data.creators || []);
      }
    } catch (error) {
      console.error('Error fetching creator map:', error);
    } finally {
      setMapLoading(false);
    }
  };

  // Lazily load the map dataset the first time the user opens Map view.
  useEffect(() => {
    if (viewMode === 'map' && authorized === true && !mapFetchedRef.current) {
      mapFetchedRef.current = true;
      fetchMapCreators();
    }
  }, [viewMode, authorized]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep ?view= in sync so the chosen view is shareable.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (viewMode === 'map') next.set('view', 'map');
    else next.delete('view');
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle search
  const handleSearch = () => {
    setSearchTerm(searchInput);
    setPage(1);
    fetchCreators(1, searchInput, sortBy);
  };
  
  // Handle sort change
  const handleSortChange = (newSort) => {
    setSortBy(newSort);
    setPage(1);
    setRandomSeed(null); // Reset seed when sort changes
    fetchCreators(1, searchTerm, newSort);
  };

  // Handle Enter key press in search input
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    setPage(1);
    setRandomSeed(null); // Reset seed for new random order
    fetchCreators(1, '', sortBy);
  };

  // Get current user ID from auth token
  useEffect(() => {
    const token = getAuthToken();
    if (token) {
      try {
        const decoded = decodeToken(token);
        if (decoded?.userId) {
          setCurrentUserId(decoded.userId);
        }
      } catch (e) {
        console.error('Failed to decode token:', e);
      }
    }
  }, []);

  // Check subscription access
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let email = params.get('email') || '';
    if (!email) {
      try { email = localStorage.getItem('postaway_email') || ''; } catch (_) {}
    }

    (async () => {
      try {
        const res = await fetch('/api/access/subscription', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email })
        });
        const data = await res.json();

        // User must have an active subscription to access creators page
        if (res.ok && data.ok && data.active) {
          setMembershipTier(data.membershipTier || 'all_access');
          if (data.membershipTier === 'basic') {
            setAuthorized('basic');
          } else {
            setAuthorized(true);
          }
        } else {
          // Redirect to home page if no active subscription
          navigate('/');
        }
      } catch (err) {
        // Redirect to home page on error
        navigate('/');
      }
    })();
  }, [navigate]);

  // Initial load
  useEffect(() => {
    if (authorized === true || authorized === 'basic') {
      fetchCreators(1, '', sortBy);
    }
  }, [authorized]);

  // Realtime search with debouncing
  useEffect(() => {
    // Skip if not authorized or searchInput hasn't been initialized or is same as searchTerm
    if (authorized !== true || searchInput === searchTerm) return;
    
    const delayDebounceFn = setTimeout(() => {
      setSearchTerm(searchInput);
      setPage(1);
      fetchCreators(1, searchInput, sortBy);
    }, 400); // 400ms delay

    return () => clearTimeout(delayDebounceFn);
  }, [searchInput, authorized]);
  
  // Handle sort change effect - skip initial mount
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (authorized === true) {
      setPage(1);
      setRandomSeed(null);
      fetchCreators(1, searchTerm, sortBy);
    }
  }, [sortBy]);

  useEffect(() => {
    if (authorized === true) {
      setPage(1);
      setRandomSeed(null);
      fetchCreators(1, searchTerm, sortBy);
    }
  }, [favoritesOnly, verifiedOnly]);

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchCreators(nextPage, searchTerm, sortBy);
    }
  };

  // Infinite scroll: auto-load next page when sentinel enters viewport
  const loadMoreSentinelRef = useRef(null);
  useEffect(() => {
    const node = loadMoreSentinelRef.current;
    if (!node) return;
    if (!hasMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          handleLoadMore();
        }
      },
      { rootMargin: '400px 0px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, loading, page, searchTerm, sortBy, creators.length]);

  const handleCardClick = (creator, imageUrl) => {
    const title = creator.first_name || (creator.handle ? displayHandle(creator.handle) : 'Creator');
    setSelectedCreator({
      ...creator,
      title,
      image: imageUrl,
    });
    setCreatorModalOpen(true);
  };

  // Open the existing creator modal from a map pin.
  const openCreatorModalFromPin = (creator) => {
    handleCardClick(creator, creator.avatar_asset_url || creator.avatar_url);
  };

  // Deep-link: open a specific creator's profile modal from ?profileId=
  // (used by "new creator joined" notifications). Falls back to a targeted
  // single-record fetch when the creator isn't on the currently loaded page.
  const handledProfileDeepLinkRef = useRef(false);
  useEffect(() => {
    if (handledProfileDeepLinkRef.current) return;
    if (authorized !== true && authorized !== 'basic') return;

    const profileId = searchParams.get('profileId');
    if (!profileId) return;

    handledProfileDeepLinkRef.current = true;

    const openCreator = (creator) => {
      if (!creator) return;
      handleCardClick(creator, creator.avatar_asset_url || creator.avatar_url);
    };

    const existing = creators.find((c) => String(c.id) === String(profileId));
    if (existing) {
      openCreator(existing);
      return;
    }

    (async () => {
      try {
        const headers = { 'Content-Type': 'application/json' };
        const token = getAuthToken();
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const response = await fetch('/api/catalogue/creators', {
          method: 'POST',
          headers,
          body: JSON.stringify({ id: profileId }),
        });
        if (response.ok) {
          const data = await response.json();
          openCreator((data.creators || [])[0]);
        }
      } catch (err) {
        console.error('Error opening creator from deep link:', err);
      }
    })();
  }, [authorized, creators, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFavoriteToggle = async (creatorId) => {
    const result = await toggleFavorite({ creatorProfileId: creatorId });
    if (result !== null) {
      setCreators(prev => prev.map(c => 
        c.id === creatorId ? { ...c, is_favorited: result } : c
      ));
      if (selectedCreator && selectedCreator.id === creatorId) {
        setSelectedCreator(prev => ({ ...prev, is_favorited: result }));
      }
    }
  };

  const handleFavoritesFilterToggle = () => {
    setFavoritesOnly(prev => !prev);
  };

  const content = (
    <div className="creators-page">
      <div className="creators-container">
        <div className="creators-header">
          <div className="creators-title">
            Our Creators
          </div>
          <div className="creators-description">
            <p>
              Discover talented content creators ready to collaborate with amazing properties and exciting destinations.
            </p>

            {MAP_ENABLED && (
              <div className="creators-view-toggle" role="tablist" aria-label="Creators view">
                <button
                  type="button"
                  role="tab"
                  aria-selected={viewMode === 'grid'}
                  className={`creators-view-toggle-btn${viewMode === 'grid' ? ' active' : ''}`}
                  onClick={() => setViewMode('grid')}
                >
                  Grid
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={viewMode === 'map'}
                  className={`creators-view-toggle-btn${viewMode === 'map' ? ' active' : ''}`}
                  onClick={() => setViewMode('map')}
                >
                  Map
                </button>
              </div>
            )}

            {/* Search Bar and Sort Dropdown */}
            {viewMode === 'grid' && (
            <div className="creators-search-container">
              <div className="creators-search-wrapper">
                <input
                  type="text"
                  placeholder="Search creators by name, handle (no @), or location..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="creators-search-input"
                  style={{ paddingRight: searchInput ? '40px' : '16px' }}
                  onFocus={(e) => e.target.style.borderColor = '#141416'}
                  onBlur={(e) => e.target.style.borderColor = '#E6E8EC'}
                />
                {searchInput && (
                  <button
                    onClick={handleClearSearch}
                    className="creators-search-clear"
                  >
                    ×
                  </button>
                )}
              </div>
              <div className="creators-sort-wrapper">
                <div className="creators-sort-icon-button">
                  <Dropdown
                    className="creators-sort-dropdown"
                    value={sortBy}
                    setValue={handleSortChange}
                    options={sortOptions}
                    iconOnly={true}
                  />
                </div>
                <FavoriteFilterButton
                  active={favoritesOnly}
                  onClick={handleFavoritesFilterToggle}
                />
                <Tippy content={verifiedOnly ? "Show all creators" : "Show only verified members"} placement="top">
                  <button
                    type="button"
                    className={`creators-verified-filter-btn${verifiedOnly ? ' active' : ''}`}
                    onClick={() => setVerifiedOnly(prev => !prev)}
                    aria-label="Filter verified members"
                  >
                    <StampMark size={22} />
                  </button>
                </Tippy>
              </div>
            </div>
            )}
            
            {viewMode === 'grid' && searchTerm && (
              <p className="creators-search-info">
                Searching for "{searchTerm}"
              </p>
            )}
            
            {viewMode === 'grid' && total > 0 && (
              <p className="creators-search-info">
                Showing {creators.length} of {total} creators
              </p>
            )}
          </div>
          {viewMode === 'map' ? (
            <Suspense fallback={<div className="creators-map-loading">Loading map…</div>}>
              {mapLoading && mapCreators.length === 0 ? (
                <div className="creators-map-loading">Loading map…</div>
              ) : (
                <CollabBoardMap
                  hosts={[]}
                  creators={mapCreators}
                  onSelectCreator={openCreatorModalFromPin}
                  onClose={() => setViewMode('grid')}
                  photosOnly
                  actionLabel="View profile"
                />
              )}
            </Suspense>
          ) : (
          <div className="creators-grid-container">
            <div className="creators-grid">
              {creators
                .filter(c => {
                  // Filter out creators without valid image URLs
                  const hasAvatarUrl = c.avatar_asset_url && c.avatar_asset_url.trim() !== '';
                  const hasAvatarUrlAlt = c.avatar_url && c.avatar_url.trim() !== '';
                  const hasValidImage = hasAvatarUrl || hasAvatarUrlAlt;
                  // Also filter out creators whose images failed to load
                  return hasValidImage && !failedImageIds.has(c.id);
                })
                .map((creator, index) => (
                  <CreatorCard 
                    key={creator.id || `creator-${index}`} 
                    creator={creator} 
                    onClick={handleCardClick}
                    onImageError={(id) => setFailedImageIds(prev => new Set([...prev, id]))}
                    onFavoriteToggle={handleFavoriteToggle}
                  />
                ))}
            </div>
            {favoritesOnly && !loading && creators.length === 0 && (
              <div className="creators-favorites-empty">
                <p>You haven't favorited any creators yet.</p>
                <p>Tap the heart icon on any creator to save them here.</p>
              </div>
            )}
            {verifiedOnly && !favoritesOnly && !loading && creators.length === 0 && (
              <div className="creators-favorites-empty">
                <p>No verified members match your search.</p>
                <p>Learn how to earn the verified stamp on the <a href="/get-verified">Get Verified</a> page.</p>
              </div>
            )}
          </div>
          )}
        </div>
      </div>

      {viewMode === 'grid' && loading && (
        <div className="creators-loading">
          <div>Loading...</div>
        </div>
      )}

      {viewMode === 'grid' && hasMore && creators.length > 0 && (
        <div
          ref={loadMoreSentinelRef}
          className="creators-load-more-sentinel"
          aria-hidden="true"
          style={{ height: '1px', width: '100%' }}
        />
      )}

      {viewMode === 'grid' && !hasMore && creators.length > 0 && (
        <div className="creators-end-message">
          <p>
            You've reached the end of our creator catalogue
          </p>
        </div>
      )}
      <CreatorModal 
        isOpen={creatorModalOpen}
        onClose={() => { setCreatorModalOpen(false); setSelectedCreator(null); }}
        creator={selectedCreator}
        currentUserId={currentUserId}
        onFavoriteToggle={handleFavoriteToggle}
      />
    </div>
  );

  // Show loading state while checking authorization
  if (authorized === null) {
    return (
      <>
        <InfoPage
          belowHeader={<div style={{ textAlign: 'center', fontFamily: "'Neue Einstellung', 'Poppins', sans-serif", color: '#0f084b' }}>Loading...</div>}
          disableStamps
          customMinHeight={600}
          showInlineLivePulse
        />
      </>
    );
  }

  if (authorized === 'basic') {
    const userType = (typeof window !== 'undefined' && window.localStorage.getItem('postaway_user_type')) || 'traveler';
    return (
      <>
        <InfoPage below={
          <BlurredGate userType={userType}>
            {content}
          </BlurredGate>
        } disableStamps customMinHeight={800} showInlineLivePulse />
      </>
    );
  }

  return (
    <>
      <InfoPage below={content} disableStamps customMinHeight={800} showInlineLivePulse />
    </>
  );
};

export default Creators;
